import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  VisibilityOutlined as VisibilityIcon,
  UploadOutlined as UploadIcon,
  DescriptionOutlined as DocumentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import UploadDocumentModal from '../../../components/committees/UploadDocumentModal';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import { csAdminApi } from '../../../services/api';

const DocumentsTab = ({ committee, onRefresh }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [docs, setDocs] = useState(committee?.documents || []);

  const fetchDocs = async () => {
    if (!committee?.id) {
      setDocs(committee?.documents || []);
      return;
    }
    try {
      const res = await csAdminApi.getDocuments('COMMITTEE', committee.id);
      setDocs(res.data?.data || []);
    } catch (err) {
      console.error('Committee documents API:', err);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committee?.id]);

  const handleUploadDocument = () => {
    setShowUploadModal(true);
  };

  const handleDeleteDocument = (doc) => {
    setDocumentToDelete(doc);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const id = documentToDelete?.document_id || documentToDelete?.id;
      if (id) {
        await csAdminApi.deleteDocument(id);
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
    } finally {
      setShowDeleteDialog(false);
      setDocumentToDelete(null);
      fetchDocs();
      if (onRefresh) onRefresh();
    }
  };

  const handleDocumentsUploaded = () => {
    fetchDocs();
    if (onRefresh) onRefresh();
  };
  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Documents</Typography>
        <Button variant="contained" size="small" startIcon={<UploadIcon />} onClick={handleUploadDocument}>
          Upload Document
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs && docs.length > 0 ? (
              docs.map((doc) => (
                <TableRow key={doc.document_id || doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          bgcolor: '#FFF3E0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DocumentIcon sx={{ fontSize: 24, color: '#E65100' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {doc.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{doc.type || doc.category || '—'}</TableCell>
                  <TableCell>
                    {(() => {
                      const d = doc.uploadDate || doc.createdAt;
                      const dt = d ? new Date(d) : null;
                      return dt && !isNaN(dt) ? dt.toLocaleDateString('en-GB') : '—';
                    })()}
                  </TableCell>
                  <TableCell>
                    {doc.size || (doc.fileSize ? `${Math.round(doc.fileSize / 1024)} KB` : '—')}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" title="View">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="primary" title="Download">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      title="Delete" 
                      onClick={() => handleDeleteDocument(doc)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No documents uploaded yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Document Modal */}
      <UploadDocumentModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleDocumentsUploaded}
        committeeId={committee?.id}
        committeeName={committee?.name}
        existingDocuments={docs || []}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDocumentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"?`}
      />
    </Paper>
  );
};

export default DocumentsTab;
